import React, { useEffect, useReducer, useState } from 'react';
import { Button } from '@patternfly/react-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { timeAgo } from '../../utilities';

/**
 * ZAP-2579: the hover toolbar under an AI Analyst chat message. It holds the
 * actions that apply to one message (today: Copy) and, when the message has a
 * known time, a relative timestamp. It is always rendered and revealed by CSS
 * on hover / focus-within, so showing it never shifts the layout; new actions
 * go in as siblings of the Copy button.
 */
type MessageToolbarProps = {
	/** what Copy puts on the clipboard */
	text: string;
	/** epoch milliseconds; omit when unknown and no time is shown */
	createdAt?: number;
};

const MessageToolbar: React.FC<MessageToolbarProps> = ({ text, createdAt }) => {
	const [copied, setCopied] = useState(false);
	// a relative time goes stale while a chat sits open: re-render once a minute
	const [, tick] = useReducer((n: number) => n + 1, 0);

	useEffect(() => {
		if (!createdAt) return undefined;
		const id = window.setInterval(tick, 60_000);
		return () => window.clearInterval(id);
	}, [createdAt]);

	useEffect(() => {
		if (!copied) return undefined;
		const id = window.setTimeout(() => setCopied(false), 1500);
		return () => window.clearTimeout(id);
	}, [copied]);

	const copy = () => {
		if (!navigator.clipboard) return;
		navigator.clipboard
			.writeText(text)
			.then(() => setCopied(true))
			.catch(() => setCopied(false));
	};

	return (
		<div
			className="chat-msg-toolbar"
			role="toolbar"
			aria-label="Message actions"
		>
			<Button
				variant="plain"
				className="chat-msg-tool"
				onClick={copy}
				aria-label={copied ? 'Copied' : 'Copy message'}
				title="Copy"
			>
				<FontAwesomeIcon icon={copied ? faCheck : faCopy} />
				<span className="chat-msg-tool-label">{copied ? 'Copied' : 'Copy'}</span>
			</Button>
			{createdAt ? (
				<span
					className="chat-msg-time"
					title={new Date(createdAt).toLocaleString()}
				>
					{timeAgo(Math.floor(createdAt / 1000))}
				</span>
			) : null}
		</div>
	);
};

export default MessageToolbar;
